import { normalizeIngredientName } from './recipeProposal';
import type { CatalogPricesProposal } from './catalogPrices';
import type { Confianza, ExpenseDraft } from './receipts';
import type { PurchaseDocumentType } from './types';

/*
 * Onboarding «Súbelo todo» (29 oct 2026, docs/product/onboarding-subir-todo.md).
 *
 * El usuario sube lo que tenga y Alexia lo lee en segundo plano. Lo leído vive
 * en un STAGING propio (ONBJOB# / ONBFILE# / ONBITEM#) y no en las entidades
 * reales: la cuenta de resultados, el catálogo y la activación no lo ven hasta
 * que el usuario pulsa «Guardar» en su grupo. Nada se guarda en firme sin que
 * lo vea, y lo dudoso se marca campo a campo.
 */

/** Tipos de documento que se distinguen. Solo factura/albaran/carta se leen en la primera entrega. */
export const TIPOS_DOCUMENTO = ['factura', 'albaran', 'carta', 'escandallo', 'ventas', 'ticket_z', 'nomina', 'otro'] as const;
export type TipoDocumento = (typeof TIPOS_DOCUMENTO)[number];

/** Lo que ya sabemos leer en el onboarding. El resto se reconoce y se manda a su importador. */
export const TIPOS_QUE_SE_LEEN: readonly TipoDocumento[] = ['factura', 'albaran', 'nomina', 'carta'];

export const NOMBRE_TIPO: Record<TipoDocumento, string> = {
  factura: 'factura',
  albaran: 'albarán',
  carta: 'carta',
  escandallo: 'escandallo',
  ventas: 'ventas',
  ticket_z: 'cierre de caja',
  nomina: 'nómina',
  otro: 'documento',
};

/** Páginas de facturas que incluye el onboarding, una sola vez y aparte de la cuota del plan (decisión 1 y 2, 29 oct 2026). */
export const PAGINAS_ONBOARDING = 150;
/**
 * Tope de gasto de IA por onboarding (decisión del diseño, 2.6). Era 3 € con
 * Textract; desde que las facturas las lee Claude (30 oct 2026, ~0,035 € por
 * factura) las 150 páginas cuestan ~6 €: con 3 el tope cortaba antes que la bolsa.
 */
export const TOPE_IA_ONBOARDING_EUR = 8;
/** Días que se guarda lo leído y no confirmado. */
export const TTL_SUBIR_TODO_DIAS = 30;

/** `preguntar`: Alexia duda qué es y se le pregunta al usuario. `no_se_lee`: se reconoce pero se carga con su importador. */
export type EstadoArchivo = 'en_cola' | 'leyendo' | 'listo' | 'preguntar' | 'error' | 'no_se_lee';

export interface ArchivoSubido {
  jobId: string;
  fileId: string;
  /** Nombre original, solo para enseñarlo. */
  nombre: string;
  contentType: string;
  key: string;
  locationId?: string;
  estado: EstadoArchivo;
  tipo?: TipoDocumento;
  tipoConfianza?: Confianza;
  /** Lo que dijo el usuario: manda sobre la clasificación. */
  tipoDicho?: TipoDocumento;
  /** «42 platos», «Frutas García · 182,40 €». */
  resumen?: string;
  error?: string;
  paginas?: number;
  intentos: number;
  creadoAt: string;
  actualizadoAt: string;
}

/** Un campo leído con lo seguros que estamos y, si hay duda, por qué. */
export interface Campo<T> {
  valor: T;
  confianza: Confianza;
  motivo?: string;
}

export type EstadoItem = 'propuesto' | 'guardado' | 'descartado';

interface ItemBase {
  jobId: string;
  itemId: string;
  fileId: string;
  estado: EstadoItem;
  locationId?: string;
  /** Lo que se creó al guardar, para enlazarlo y para que reintentar no duplique. */
  guardadoComo?: { expenseId?: string; recipeId?: string; menuItemId?: string; catalogDraftId?: string };
  error?: string;
  actualizadoAt: string;
}

export interface ItemGasto extends ItemBase {
  kind: 'gasto';
  documentType: PurchaseDocumentType;
  propuesta: ExpenseDraft;
  precios?: CatalogPricesProposal;
  /** Cuándo se subió el documento: la prueba de que lo cargó el dueño (activación). */
  cargadoAt?: string;
}

export interface ItemPlato extends ItemBase {
  kind: 'plato';
  nombre: Campo<string>;
  seccion?: string;
  /** PVP con impuestos, como en la carta. Sin precio legible: valor null. */
  precio: Campo<number | null>;
  descripcion?: string;
  /** Ingredientes que dice la carta. Solo pistas para la receta: no se crean. */
  ingredientesMencionados: string[];
  /** Ya está en «Tu carta» con este nombre: no se vuelve a crear. */
  yaEnCarta?: { menuItemId: string; precio: number };
}

export type ItemSubido = ItemGasto | ItemPlato;

export interface TrabajoSubirTodo {
  jobId: string;
  tenantId: string;
  creadoPor: string;
  creadoAt: string;
  actualizadoAt: string;
  /** Páginas de facturas leídas con la bolsa del onboarding. */
  paginasLeidas: number;
  costeIaEur: number;
}

/* ------------------------------------------------------------------ */
/* Clasificación: reglas baratas antes de preguntar a Alexia           */
/* ------------------------------------------------------------------ */

const PISTAS_NOMBRE: Array<[RegExp, TipoDocumento]> = [
  [/\b(albar[aá]n(es)?|alb\b|nota de (venta|entrega)|remisi[oó]n)/, 'albaran'],
  [/\b(factura|fra\b|fact\b|invoice|cfdi)/, 'factura'],
  [/\b(carta|men[uú]|menu)\b/, 'carta'],
  [/\b(escandallos?|fichas? t[eé]cnicas?|recetas?)\b/, 'escandallo'],
  [/\b(n[oó]minas?|payroll)\b/, 'nomina'],
  [/\b(cierre|corte de caja|informe z|z report|arqueo)\b/, 'ticket_z'],
  [/\b(ventas|sales|facturaci[oó]n diaria)\b/, 'ventas'],
];

/**
 * Pista por nombre de archivo y tipo. Es una pista, no una decisión: «carta
 * del proveedor.pdf» existe. Alexia decide y la pista solo la acompaña.
 */
export function pistaDeArchivo(nombre: string, contentType: string): TipoDocumento | undefined {
  const texto = nombre.toLowerCase().replace(/[_\-.]+/g, ' ');
  for (const [patron, tipo] of PISTAS_NOMBRE) if (patron.test(texto)) return tipo;
  if (/spreadsheet|excel|csv/.test(contentType)) return undefined;
  return undefined;
}

/**
 * Del tipo que propone Alexia al tipo con el que se trabaja. Lo que dice el
 * usuario manda siempre; con confianza baja y una pista distinta del nombre
 * se pregunta («¿Esto es una factura o un albarán?») en vez de adivinar.
 */
export function decidirTipo(input: {
  propuesto?: { tipo: TipoDocumento; confianza: Confianza };
  pista?: TipoDocumento;
  dicho?: TipoDocumento;
}): { tipo?: TipoDocumento; confianza: Confianza; preguntar: boolean } {
  if (input.dicho) return { tipo: input.dicho, confianza: 'alta', preguntar: false };
  const p = input.propuesto;
  if (!p) return input.pista ? { tipo: input.pista, confianza: 'media', preguntar: false } : { confianza: 'baja', preguntar: true };
  if (p.confianza === 'baja') return { tipo: p.tipo, confianza: 'baja', preguntar: true };
  if (input.pista && input.pista !== p.tipo && p.confianza === 'media') {
    // «factura» en el nombre y Alexia ve un albarán: las dos cosas son posibles.
    return { tipo: p.tipo, confianza: 'baja', preguntar: true };
  }
  return { tipo: p.tipo, confianza: p.confianza, preguntar: false };
}

/* ------------------------------------------------------------------ */
/* Carta                                                               */
/* ------------------------------------------------------------------ */

/** Lo que devuelve Alexia por página (el esquema de la herramienta `leer_carta`). */
export interface CartaLeida {
  secciones: Array<{
    nombre?: string;
    platos: Array<{
      nombre: string;
      /** El precio como está escrito: «12,50 €», «12.5», «S/M», «9 / 16». */
      precioTexto?: string;
      descripcion?: string;
      ingredientesMencionados?: string[];
      /** Alexia no lo ve claro (borroso, cortado, tachado). */
      dudoso?: boolean;
    }>;
  }>;
}

/**
 * «12,50 €» → 12.5. Devuelve null cuando no hay un precio único y claro:
 * «S/M» (según mercado), «9 / 16» (media y ración), vacío. No se adivina.
 */
export function precioDeCarta(texto: string | undefined): { valor: number | null; motivo?: string } {
  const t = (texto ?? '').trim();
  if (!t) return { valor: null, motivo: 'Sin precio en la carta' };
  if (/\b(s\/?m|seg[uú]n mercado|consultar|p\.?\s?v\.?\s?p|market)\b/i.test(t)) return { valor: null, motivo: 'Precio según mercado' };
  const numeros = t.match(/\d{1,3}(?:[.,]\d{3})+(?:[.,]\d{1,2})?|\d+(?:[.,]\d{1,2})?/g) ?? [];
  if (numeros.length === 0) return { valor: null, motivo: 'No se lee el precio' };
  if (numeros.length > 1) return { valor: null, motivo: `Hay varios precios (${t}): elige el de la ración que vendes` };
  const crudo = numeros[0]!;
  // «1.250» con grupos de tres es un millar (pesos); «12.50» y «12,50» son céntimos.
  const millares = crudo.match(/^(\d{1,3}(?:[.,]\d{3})+)(?:[.,](\d{1,2}))?$/);
  const valor = millares
    ? Number(`${millares[1]!.replace(/[.,]/g, '')}.${millares[2] ?? '0'}`)
    : Number(crudo.replace(',', '.'));
  if (!Number.isFinite(valor) || valor <= 0) return { valor: null, motivo: 'No se lee el precio' };
  return { valor: Math.round(valor * 100) / 100 };
}

/** Un precio de carta raro (más de 500 o céntimos que no son de carta): se confirma. */
function precioRaro(valor: number): string | undefined {
  if (valor > 500) return 'Precio muy alto para un plato: confírmalo';
  const centimos = Math.round(valor * 100) % 100;
  if (centimos !== 0 && centimos % 5 !== 0) return 'Céntimos poco habituales: confírmalo';
  return undefined;
}

export interface PlatoDeCarta {
  nombre: Campo<string>;
  seccion?: string;
  precio: Campo<number | null>;
  descripcion?: string;
  ingredientesMencionados: string[];
}

/**
 * De las páginas que leyó Alexia a una lista de platos sin repetidos. Un plato
 * que sale en dos páginas (la carta y la sugerencia del día) es el mismo si
 * se llama igual; si los precios no coinciden, se marca para confirmar.
 */
export function platosDeCarta(paginas: CartaLeida[]): PlatoDeCarta[] {
  const porNombre = new Map<string, PlatoDeCarta>();
  for (const pagina of paginas) {
    for (const seccion of pagina.secciones) {
      for (const leido of seccion.platos) {
        const nombre = leido.nombre.replace(/\s+/g, ' ').trim();
        if (!nombre) continue;
        const clave = normalizeIngredientName(nombre);
        const { valor, motivo } = precioDeCarta(leido.precioTexto);
        const raro = valor != null ? precioRaro(valor) : undefined;
        const precio: Campo<number | null> = valor == null
          ? { valor: null, confianza: 'baja', motivo }
          : leido.dudoso
            ? { valor, confianza: 'baja', motivo: 'Alexia no lo ve claro en la foto' }
            : raro ? { valor, confianza: 'media', motivo: raro } : { valor, confianza: 'alta' };
        const plato: PlatoDeCarta = {
          nombre: leido.dudoso ? { valor: nombre, confianza: 'media', motivo: 'Alexia no lo ve claro en la foto' } : { valor: nombre, confianza: 'alta' },
          ...(seccion.nombre?.trim() ? { seccion: seccion.nombre.trim() } : {}),
          precio,
          ...(leido.descripcion?.trim() ? { descripcion: leido.descripcion.trim() } : {}),
          ingredientesMencionados: [...new Set((leido.ingredientesMencionados ?? []).map((i) => i.trim()).filter(Boolean))].slice(0, 20),
        };
        const previo = porNombre.get(clave);
        if (!previo) { porNombre.set(clave, plato); continue; }
        if (previo.precio.valor == null && plato.precio.valor != null) previo.precio = plato.precio;
        else if (previo.precio.valor != null && plato.precio.valor != null && previo.precio.valor !== plato.precio.valor) {
          previo.precio = { valor: previo.precio.valor, confianza: 'baja', motivo: `Sale a dos precios: ${previo.precio.valor} y ${plato.precio.valor}` };
        }
        previo.ingredientesMencionados = [...new Set([...previo.ingredientesMencionados, ...plato.ingredientesMencionados])];
      }
    }
  }
  return [...porNombre.values()];
}

/* ------------------------------------------------------------------ */
/* Lo que hay que confirmar                                            */
/* ------------------------------------------------------------------ */

/**
 * Campos dudosos de un item, en palabras del dueño. Vacío = «Todo claro».
 * Un item con dudas no se guarda hasta que el usuario lo toca (lo edita o lo
 * confirma): `PATCH` limpia sus dudas.
 */
export function dudasDe(item: ItemSubido): string[] {
  if (item.kind === 'plato') {
    const dudas: string[] = [];
    if (item.nombre.confianza !== 'alta') dudas.push(item.nombre.motivo ?? 'Revisa el nombre');
    if (item.precio.valor == null) dudas.push(item.precio.motivo ?? 'Falta el precio');
    else if (item.precio.confianza !== 'alta') dudas.push(item.precio.motivo ?? 'Revisa el precio');
    return dudas;
  }
  const p = item.propuesta;
  const dudas: string[] = [];
  if (p.confianza.importe !== 'alta') dudas.push('Revisa el importe');
  if (p.confianza.fecha !== 'alta') dudas.push('Revisa la fecha');
  if (p.confianza.proveedor === 'baja') dudas.push('Revisa el proveedor');
  if (p.duplicado) dudas.push('Parece repetida con una que ya tienes');
  dudas.push(...p.avisos);
  return [...new Set(dudas)];
}

/** Lo que se ve en «Para confirmar»: los propuestos con alguna duda. */
export function porConfirmar(items: ItemSubido[]): ItemSubido[] {
  return items.filter((item) => item.estado === 'propuesto' && dudasDe(item).length > 0);
}
